import { __assign } from "tslib";
import * as React from 'react';
import { MenuItem, InputLabel, FormControl, Select, makeStyles } from '@material-ui/core';
import { useField } from 'formik';
import { BLayout } from '../BLayout';
import { BError } from './BError';
var useStyles = makeStyles(function (theme) { return ({
    select: {
        "&:focus": {
            // Resolves a bug in Material UI's Select component where the focused background 
            // color fills the area without inheriting the input's border radius. 
            // Note: this does the same thing as the input field and pulls from the project's
            // Material UI Theme's shape.borderRadius property. 
            // If the input's border radius is directly changed, the focus background's 
            // border radius would not match it unless you override its classes.
            borderRadius: theme.shape.borderRadius
        }
    }
}); });
/**
 * **Must be a child of BForm**
 *
 * `BSelect` provides a Material UI Select Component.
 *
 *  **Required Props:**
 *  - name
 *   - must match the name from initialValues
 *
 * **Optional Props:**
 * - label
 *  - the label content.
 * - required
 *  - default is false, if true the select is required to have value(s) selected
 * - options
 *  - a list of options to be displayed in the select menu
 * - multiple
 *  - default is false, if true the select allows multiple options to be selected
 * - onChange
 *  - function that runs when user modifies the component
 * - [selectProps](https://gitlab.com/2020-bootcamp/forms/mui-bueno/-/blob/development/src/@types/material.ts)
 *  - a number of select props that can be used to customize the component
 * - [BLayout](https://gitlab.com/2020-bootcamp/forms/mui-bueno/-/blob/update-docs/documentations/tag-references/MWidget.md)
 *  - custom gridding, sizes, and alignment
 *
 * **Basic Example:**
 * ```jsx
 * <BSelect
 *   name='color'
 *   label='Colors'
 *   options={colorOptions}
 * />
 * ```
 *
 * API:
 *
 * - [BSelect API](https://gitlab.com/2020-bootcamp/forms/mui-bueno/-/blob/update-docs/docs/tag-references/input/BSelect.md)
 */
export var BSelect = function (props) {
    var name = props.name, _a = props.options, options = _a === void 0 ? [] : _a, _b = props.label, label = _b === void 0 ? name : _b, _c = props.required, required = _c === void 0 ? false : _c, _d = props.multiple, multiple = _d === void 0 ? false : _d, _e = props.onChange, onChange = _e === void 0 ? undefined : _e, _f = props.full, full = _f === void 0 ? true : _f, _g = props.variant, variant = _g === void 0 ? 'standard' : _g, _h = props.w, w = _h === void 0 ? 2 : _h, className = props.className;
    var classes = useStyles();
    // Formik boilerplate ------------------
    var _j = useField({ name: name, validate: function () { return isRequired(); } }), field = _j[0], helpers = _j[2];
    var setValue = helpers.setValue;
    var value = field.value;
    // -------------------------------------
    var isRequired = function () {
        if (required && !value) {
            return props.label + " is required";
        }
        else {
            return '';
        }
    };
    var singleIndex = function () {
        var index = '';
        for (var i = 0; i < options.length; i++) {
            if (options[i].value.toString() === value) {
                index = i.toString();
            }
        }
        return index;
    };
    var multipleIndex = function () {
        var indices = [];
        var values = value.toString().split(",");
        for (var i = 0; i < values.length; i++) {
            for (var j = 0; j < options.length; j++) {
                if (options[j].value.toString() === values[i]) {
                    indices.push(j);
                }
            }
        }
        return indices;
    };
    var _k = React.useState(multiple ? multipleIndex : singleIndex), index = _k[0], setIndex = _k[1]; // tracks selected option  
    var items = options.map(function (o, i) {
        return (React.createElement(MenuItem, { id: name + "-option-" + i, value: i, key: i.toString(), disabled: o.disabled }, o.label));
    });
    var onChangeWrapped = function (event) {
        if (multiple) {
            var vals_1 = [];
            event.target.value.forEach(function (idx) {
                if (options[idx].value) {
                    vals_1.push(options[idx].value);
                }
            });
            setValue(vals_1);
            if (onChange)
                onChange(event, vals_1);
        }
        else {
            setValue(options[event.target.value].value);
            if (onChange && options[event.target.value].value !== undefined) {
                onChange(event, options[event.target.value].value);
            }
        }
        setIndex(event.target.value);
    };
    return (React.createElement(BLayout, __assign({}, props, { full: full, w: w }),
        React.createElement("div", { className: className },
            React.createElement(FormControl, { fullWidth: true, variant: variant },
                React.createElement(InputLabel, { htmlFor: name }, label),
                React.createElement(Select, __assign({}, props.selectProps, { id: name + "-sel", name: name, value: index }, (label == '' ? {} : { label: label }), { multiple: multiple, onChange: onChangeWrapped, MenuProps: { getContentAnchorEl: null }, classes: { select: classes.select } }), items),
                React.createElement(BError, { name: name, id: name + "-err" })))));
};
//# sourceMappingURL=BSelect.js.map